import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { type HTMLAttributes } from 'react';

import { cn } from '../utils';

const progressBarVariants = cva(
  'relative h-8 w-full overflow-hidden rounded-sm bg-muted shadow-sm border border-border',
  {
    variants: {
      variant: {
        primary: 'bg-primary/15',
        muted: 'bg-muted',
        success: 'bg-success/15',
        warning: 'bg-warning/15',
      },
    },
  },
);

export type ProgressBarVariant = VariantProps<typeof progressBarVariants>;

const ProgressBar = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> &
    ProgressBarVariant
>(({ className, variant, ...props }, ref) => {
  return (
    <ProgressPrimitive.Root
      className={cn(progressBarVariants({ variant }), className)}
      ref={ref}
      {...props}
    />
  );
});
ProgressBar.displayName = ProgressPrimitive.Root.displayName;

const progressBarIndicatorVariants = cva(
  'absolute h-full transition-all duration-500 rounded-sm',
  {
    variants: {
      variant: {
        primary: 'bg-primary/80',
        muted: 'bg-muted-foreground/80',
        success: 'bg-success/80',
        warning: 'bg-warning/80',
      },
    },
  },
);

export type ProgressBarIndicatorVariant = VariantProps<
  typeof progressBarIndicatorVariants
>;

const ProgressBarIndicator = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Indicator>,
  HTMLAttributes<HTMLDivElement> & ProgressBarIndicatorVariant
>(({ variant, className, ...props }, ref) => {
  return (
    <ProgressPrimitive.Indicator
      ref={ref}
      className={cn(progressBarIndicatorVariants({ variant }), className)}
      {...props}
    />
  );
});
ProgressBarIndicator.displayName = ProgressPrimitive.Indicator.displayName;

export { ProgressBar, ProgressBarIndicator };
